import { css } from 'styled-components';

import { ButtonTheme, SternumTheme } from '@sternum/ui';

import { Colors } from '../constants';

import { padding } from './shared';

const borderRadius = 6;

export function lightPrimaryTheme(theme: SternumTheme): ButtonTheme.ButtonVariantTheme {
  return {
    ...ButtonTheme.primaryTheme(theme),
    backgroundColor: Colors.BrandBlue,
    textColor: Colors.White,
    border: 'none',
    borderRadius: borderRadius,

    hoverBackgroundColor: Colors.BrandBlueMiddle,
    hoverTextColor: Colors.White,
    hoverBorder: 'none',
    hoverBorderRadius: borderRadius,

    disabledBackgroundColor: Colors.GreyDarkerMiddle,
    disabledTextColor: Colors.White,
    disabledBorder: 'none',
    disabledBorderRadius: borderRadius,

    padding,
    fontSize: 12,
    fontWeight: 600,
    focusColor: Colors.AquaMiddle,
    lineHeight: '20px',

    buttonCss: ({ size }) => css`
      max-height: ${size === 'large' ? '38px' : '34px'};
      min-width: 90px;
    `,

    leftIconContainerCss: css`
      margin-right: 8px;
    `,
    rightIconContainerCss: css`
      margin-left: 8px;
    `,
  };
}

export function lightSecondaryTheme(theme: SternumTheme): ButtonTheme.ButtonVariantTheme {
  return {
    ...ButtonTheme.secondaryTheme(theme),
    backgroundColor: Colors.BrandBlueLightest,
    textColor: Colors.BrandBlue,
    border: `1px solid ${Colors.VioletMiddleLighter}`,
    borderRadius: borderRadius,

    hoverBackgroundColor: Colors.Transparent,
    hoverTextColor: Colors.BrandBlue,
    hoverBorder: `1px solid ${Colors.BrandBlue}`,
    hoverBorderRadius: borderRadius,

    disabledBackgroundColor: Colors.GreyDarkerMiddle,
    disabledTextColor: Colors.White,
    disabledBorder: 'none',
    disabledBorderRadius: borderRadius,

    padding,
    fontSize: 12,
    fontWeight: 600,
    focusColor: Colors.AquaMiddle,
    lineHeight: '20px',

    buttonCss: ({ size }) => css`
      max-height: ${size === 'large' ? '38px' : '34px'};
      min-width: 90px;
    `,

    leftIconContainerCss: css`
      margin-right: 8px;
    `,
    rightIconContainerCss: css`
      margin-left: 8px;
    `,
  };
}

export function lightTertiaryTheme(theme: SternumTheme): ButtonTheme.ButtonVariantTheme {
  return {
    ...ButtonTheme.tertiaryTheme(theme),
    backgroundColor: 'transparent',
    textColor: Colors.DarkerText,
    border: `1px solid ${Colors.GreyDarker}`,
    borderRadius: borderRadius,

    hoverBackgroundColor: Colors.Light,
    hoverTextColor: Colors.DarkerText,
    hoverBorder: `1px solid ${Colors.Light}`,
    hoverBorderRadius: borderRadius,

    disabledBackgroundColor: 'transparent',
    disabledTextColor: Colors.GreyDarkerMiddle,
    disabledBorder: `1px solid ${Colors.GreyDarkerMiddle}`,
    disabledBorderRadius: borderRadius,

    padding,
    fontSize: 12,
    fontWeight: 600,
    focusColor: Colors.AquaMiddle,
    lineHeight: '20px',

    buttonCss: ({ size }) => css`
      max-height: ${size === 'large' ? '38px' : '34px'};
      min-width: 90px;
    `,

    leftIconContainerCss: css`
      margin-right: 8px;
    `,
    rightIconContainerCss: css`
      margin-left: 8px;
    `,
  };
}

export function lightTextTheme(theme: SternumTheme): ButtonTheme.ButtonVariantTheme {
  return {
    ...ButtonTheme.textTheme(theme),
    backgroundColor: 'transparent',
    textColor: Colors.BrandBlue,

    hoverBackgroundColor: 'transparent',
    hoverTextColor: Colors.BrandBlueMiddle,

    disabledBackgroundColor: 'transparent',
    disabledTextColor: Colors.GreyDarkerMiddle,

    padding: 0,
    fontSize: 12,
    fontWeight: 600,
    focusColor: Colors.AquaMiddle,
    lineHeight: '20px',

    buttonCss: ({ size }) => {
      if (size === 'large') {
        return css`
          font-size: 14px;
          line-height: 20px;
        `;
      }

      return css`
        font-size: 12px;
        line-height: 18px;
      `;
    },
  };
}

export function lightTextSecondaryTheme(theme: SternumTheme): ButtonTheme.ButtonVariantTheme {
  return {
    ...ButtonTheme.textTheme(theme),
    backgroundColor: 'transparent',
    textColor: Colors.DarkerText,

    hoverBackgroundColor: 'transparent',
    hoverTextColor: Colors.BrandBlueMiddle,

    disabledBackgroundColor: 'transparent',
    disabledTextColor: Colors.GreyDarkerMiddle,

    padding: 0,
    fontSize: 12,
    fontWeight: 600,
    focusColor: Colors.AquaMiddle,
    lineHeight: '20px',

    buttonCss: ({ size }) => {
      if (size === 'large') {
        return css`
          font-size: 16px;
          line-height: 24px;
        `;
      }

      return css`
        font-size: 14px;
        line-height: 20px;
      `;
    },

    leftIconContainerCss: css`
      margin-right: 8px;
    `,
    rightIconContainerCss: css`
      margin-left: 8px;
    `,
  };
}

export function lightErrorPrimaryTheme(theme: SternumTheme): ButtonTheme.ButtonVariantTheme {
  return {
    ...ButtonTheme.primaryTheme(theme),
    backgroundColor: Colors.BrandPink,
    textColor: Colors.White,
    border: 'none',
    borderRadius: borderRadius,

    hoverBackgroundColor: Colors.BrandPinkDark,
    hoverTextColor: Colors.White,
    hoverBorder: 'none',
    hoverBorderRadius: borderRadius,

    disabledBackgroundColor: Colors.GreyDarkerMiddle,
    disabledTextColor: Colors.White,
    disabledBorder: 'none',
    disabledBorderRadius: borderRadius,

    padding,
    fontSize: 12,
    fontWeight: 600,
    focusColor: Colors.AquaMiddle,
    lineHeight: '20px',

    buttonCss: ({ size }) => css`
      max-height: ${size === 'large' ? '38px' : '34px'};
      min-width: 90px;
    `,

    leftIconContainerCss: css`
      margin-right: 8px;
    `,
    rightIconContainerCss: css`
      margin-left: 8px;
    `,
  };
}

export function lightWidgetTheme(theme: SternumTheme): ButtonTheme.ButtonVariantTheme {
  return {
    ...ButtonTheme.primaryTheme(theme),
    backgroundColor: Colors.TheLightest,
    textColor: Colors.BrandBlue,
    border: `1px solid ${Colors.BrandBlueLight}`,
    borderRadius: borderRadius,

    hoverBackgroundColor: Colors.BrandBlueLightest,
    hoverTextColor: Colors.BrandBlueMiddle,
    hoverBorder: `1px solid ${Colors.BrandBlueLight}`,
    hoverBorderRadius: borderRadius,

    disabledBackgroundColor: Colors.TheLightest,
    disabledTextColor: Colors.GreyDarkerMiddle,
    disabledBorder: `1px solid ${Colors.BrandBlueLight}`,
    disabledBorderRadius: borderRadius,

    padding,
    fontSize: 12,
    fontWeight: 600,
    focusColor: Colors.AquaMiddle,
    lineHeight: '20px',

    buttonCss: css`
      height: 135px;
      width: 264px;
    `,

    leftIconContainerCss: css`
      margin-right: 8px;
    `,
    rightIconContainerCss: css`
      margin-left: 8px;
    `,
  };
}

export function SBOMLightButtonTheme(theme: SternumTheme): ButtonTheme.ButtonTheme {
  return {
    primary: lightPrimaryTheme(theme),
    secondary: lightSecondaryTheme(theme),
    tertiary: lightTertiaryTheme(theme),
    text: lightTextTheme(theme),
    'text-secondary': lightTextSecondaryTheme(theme),
    'error-primary': lightErrorPrimaryTheme(theme),
    widget: lightWidgetTheme(theme),
  };
}
